import {
  Application,
  Container,
  Graphics,
  RenderTexture,
  Sprite,
  Text,
  Texture,
  TextureStyle,
} from 'pixi.js';
import type { SimEvent, StatusState, World } from '@prism/sim';
import {
  BACKGROUND,
  LOGICAL_H,
  LOGICAL_W,
  MAP_COLS,
  MAP_ROWS,
  TILE_PX,
  computeScale,
} from './constants.js';
import { enemyTextureKey, loadAssets, type AssetBundle } from './assets.js';
import { MAX_STATUS_PIPS, PIP_GAP, PIP_PX, pipTints, statusMaskOf } from './statusPips.js';
import { TowerView, type TowerVisual } from './towerView.js';
import { RangeCircle, type BlockedFn } from './rangeCircle.js';
import { starRange } from '@prism/sim';
import { VfxRenderer } from '../vfx/vfxRenderer.js';
import type { Vfx } from '../vfx/vfx.js';

/**
 * The battlefield renderer (TRD-03).
 *
 * Reads world state; never decides anything. Every gameplay question — what is placeable,
 * what a tower's range is — is answered by the sim, because a renderer that reimplements a
 * rule is a renderer that will eventually disagree with the simulation the server replays.
 */

export interface MapView {
  groundLayer: readonly number[];
  overlayLayer: readonly number[];
  buildMask: readonly string[];
  /**
   * Buff tiles, resolved to presentation by the caller so the renderer never imports
   * content. Colour and particle come from the authored kind; the renderer only draws.
   */
  buffTiles?: readonly { x: number; y: number; color: number; particle: string }[];
}

/** Supplied by the caller so the renderer never imports content directly. */
export type TowerVisualLookup = (defId: string) => Omit<TowerVisual, 'star' | 'defId'>;

interface EnemyView {
  sprite: Sprite;
  bar: Container;
  barFill: Sprite;
  /**
   * Chromatic ghost, built only for enemies that glitch (Prism).
   *
   * A second copy of the silhouette, tinted and offset a pixel or two per frame. It is
   * the cheapest possible "this thing is not playing by the rules" read at 16px — no
   * filter, no shader, one extra sprite on exactly one enemy in the game.
   */
  ghostA: Sprite | null;
  ghostB: Sprite | null;
  /** The Aegis's frontal arc, rotated to its heading. Built lazily — see the sync loop. */
  plate: Sprite | null;
  /**
   * The shield bubble, built only for enemies that HAVE a shield phase. Visible exactly
   * while the bar holds, so "the shield is down" is legible on the board itself and not
   * only in the HUD — the player is looking at the boss, not at the top of the screen.
   */
  shieldRing: Sprite | null;
  /**
   * Status pips (R-03-16), built the first time this enemy is actually afflicted.
   *
   * Lazy because four extra sprites on every one of 400 enemies is 1600 display objects
   * standing around invisible for the many enemies that die before anything touches them.
   */
  pips: Container | null;
  /** Last drawn status mask, so the pip row is rebuilt on change and not per frame. */
  statusMask: number;
  prevX: number;
  prevY: number;
  flashing: boolean;
}

/**
 * One hitscan line, in PIXELS, with its own decay.
 *
 * Stored in pixel space because it is redrawn on real frames rather than on ticks —
 * converting once at the event is cheaper than converting N times per second forever.
 */
interface BeamLine {
  tower: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  color: number;
  weapon: 'beam' | 'laser' | 'chain';
  chainHop: number;
  lifeMs: number;
  maxLifeMs: number;
  /**
   * Chain only: perpendicular midpoint offsets in pixels, one per interior vertex,
   * rolled ONCE when the arc lands (R-03-20) and never re-rolled.
   *
   * Rolling per frame reads as white noise — at 60 fps the bolt has no shape long enough
   * for the eye to see one. Rolling once gives each arc a jagged identity that holds for
   * its 90 ms, which is what actually reads as electricity. The count varies 3–5 per arc
   * for the same reason: two simultaneous arcs with identical structure read as a
   * rendering artefact.
   *
   * The array is reused across the pool (`length` is truncated in place), so a Tempest
   * board at wave 25 allocates nothing per shot.
   */
  jitter: number[];
}

/** R-03-20: 3–5 midpoint segments per arc. */
const CHAIN_JITTER_MIN = 3;
const CHAIN_JITTER_MAX = 5;

/** Peak perpendicular displacement: ~a third of a tile. Enough to break the straight
 * line without the bolt ever appearing to miss the enemy it is hitting. */
const CHAIN_JITTER_PX = 5;

/** The one archetype that glitches. Kept here so the renderer never guesses from flags. */
const GLITCH_ARCHETYPE = 'boss-prism';

/**
 * Emit one trail mote every N projectile-syncs.
 *
 * Counted across ALL projectiles rather than per projectile on purpose: the thing that
 * needs bounding is the total spend, and a single fast shot deserves a denser trail than
 * one of thirty simultaneous ones.
 */
const TRAIL_EVERY_N_FRAMES = 2;

/** The same damage-kind palette the impact particles use, so a beam matches its hit. */
const BEAM_TINT: Record<'physical' | 'magic' | 'true', number> = {
  physical: 0xffffff,
  magic: 0x9b5de5,
  true: 0xffd60a,
};

/** Every beam's core is near-white — the tint is carried by the glow around it. */
const BEAM_CORE = 0xf6f7ff;

/** How long a one-shot beam or chain arc stays on screen. Long enough to read at 4×. */
const BEAM_FLASH_MS = 90;

export class Renderer {
  readonly app = new Application();

  /** Layers 0–7, bottom to top (TRD-03 §4). */
  private readonly world = new Container();
  private readonly bgLayer = new Container();
  private readonly rangeLayer = new Container();
  private readonly entityLayer = new Container();
  private readonly overlayLayer = new Container();
  private readonly projectileLayer = new Container();
  private readonly particleLayer = new Container();
  private readonly uiWorldLayer = new Container();

  /**
   * Screen-space overlay, OUTSIDE the shaken world container.
   *
   * The core-damage vignette must cover the viewport steadily while everything under it
   * shakes; putting it inside `world` would swing a red rectangle around and leave gaps
   * at the edges.
   */
  private readonly screenLayer = new Container();

  /** Exactly two Graphics in the whole renderer (TRD-03 §10) — beams, and debug. */
  private readonly beams = new Graphics();
  private readonly debug = new Graphics();

  private assets!: AssetBundle;
  private rangeTexture!: Texture;
  private plateTexture!: Texture;
  private sightFillTexture!: Texture;
  private sightRingTexture!: Texture;
  private lookup: TowerVisualLookup = () => ({ colorA: 0xffffff, colorB: 0xffffff, glyph: '?' });

  private readonly towers = new Map<number, TowerView>();
  private readonly enemies = new Map<number, EnemyView>();
  private readonly projectiles = new Map<number, Sprite>();
  /** Swarm/broodmother drones (B-17) — tiny motes tinted by their owner tower. */
  private readonly droneSprites = new Map<number, Sprite>();
  /** Zone attacks (B-18/B-19) — a ring that telegraphs, then pulses with each strike. */
  private readonly zoneSprites = new Map<number, Sprite>();

  /**
   * Hitscan lines waiting to be drawn — beams, laser channels, chain arcs.
   *
   * They are held here rather than drawn on arrival because `handleEvents` runs on a sim
   * tick and `update` runs on a real frame; at 4× speed four ticks of beams arrive before
   * one frame is drawn, and at 0 enemies a whole second of frames arrives between ticks.
   * A sustained laser has to survive the gap or it strobes.
   */
  private readonly beamLines: BeamLine[] = [];

  /** Reused by `syncStatusPips` so a wave of afflicted enemies allocates nothing. */
  private readonly pipScratch: number[] = [];

  private ghost: Container | null = null;
  private ghostBase: Sprite | null = null;
  private ghostRing: Sprite | null = null;
  private ghostGlyph: Text | null = null;
  /** Which def the ghost is currently dressed as — retint only on change. */
  private ghostDef = '';
  private ghostRange: RangeCircle | null = null;
  /** Range circle for the hovered-or-selected tower (playtest UX request). */
  private focusRange: RangeCircle | null = null;
  /**
   * What stops a shot, read off the build mask the map already carries. Every range
   * indicator is clipped by it, so a circle never claims ground behind a rock.
   */
  private blockedSight: BlockedFn | null = null;
  private hoveredTower: number | null = null;

  /** Placed towers whose merge is ready — fed from the snapshot (B-5), never computed here. */
  private readonly mergeHints = new Set<number>();

  // ── Spawn/Core identity (B-8): rings, labels, and the build-phase path flow ─────────
  private spawnMarks: { sprite: Sprite; base: number }[] = [];
  private coreMark: { sprite: Sprite; base: number } | null = null;
  private wayLabels: Text[] = [];
  /** Buff-tile markers, rebuilt with the map (playtest request). */
  private buffMarks: { sprite: Sprite; base: number; x: number; y: number; color: number; particle: string }[] = [];
  /** Wall-clock accumulator for the tiles' ambient shimmer. */
  private buffPulse = 0;
  private chevronTexture!: Texture;
  private flow: { sprite: Sprite; dist: number }[] = [];
  private flowBuilt = false;
  private markTime = 0;
  /** Wall-clock ms until the teleport tear stops widening the glitch. */
  private glitchUntil = 0;

  /** Towers of the synergy row being hovered (R-03-24). Empty set = no highlight. */
  private readonly classHighlight = new Set<number>();
  /** Pooled range circles for the class highlight — grown on demand, never destroyed. */
  private readonly classRangeSprites: RangeCircle[] = [];
  /** Live rings on surging support towers, keyed by tower id. */
  private readonly surgeRings = new Map<number, Sprite>();
  /** Rings marking towers a Disabler has knocked out, keyed by tower id. */
  private readonly disableRings = new Map<number, Sprite>();
  private mapData: MapView | null = null;

  private scale = 1;
  private vfx: Vfx | null = null;
  private vfxRenderer: VfxRenderer | null = null;
  debugVisible = false;

  static async create(parent: HTMLElement, lookup: TowerVisualLookup): Promise<Renderer> {
    const r = new Renderer();
    await r.init(parent, lookup);
    return r;
  }

  private async init(parent: HTMLElement, lookup: TowerVisualLookup): Promise<void> {
    // Before ANY texture loads, or whichever slipped through gets bilinear filtering.
    TextureStyle.defaultOptions.scaleMode = 'nearest';

    await this.app.init({
      width: LOGICAL_W,
      height: LOGICAL_H,
      backgroundColor: BACKGROUND,
      antialias: false,
      roundPixels: true,
      autoDensity: false,
      resolution: 1,
      preference: 'webgl',
    });

    this.lookup = lookup;
    this.assets = await loadAssets();

    // Only the entity layer sorts. Enabling it elsewhere is the most common Pixi
    // performance mistake (TRD-03 R-03-05).
    this.entityLayer.sortableChildren = true;

    this.world.addChild(
      this.bgLayer,
      this.rangeLayer,
      this.entityLayer,
      this.overlayLayer,
      this.projectileLayer,
      this.beams,
      this.particleLayer,
      this.uiWorldLayer,
    );
    this.app.stage.addChild(this.world, this.screenLayer, this.debug);

    // Rotate about the centre so shake pivots the view rather than swinging it from the
    // top-left corner (TRD-04 R-04-09).
    this.world.pivot.set(LOGICAL_W / 2, LOGICAL_H / 2);
    this.world.position.set(LOGICAL_W / 2, LOGICAL_H / 2);

    this.rangeTexture = this.makeRangeTexture();
    // Two more for the sight-clipped indicators. The covered fill is heavier than the
    // zone circle's because it now has to be told apart from the blocked part at a
    // glance; the blocked part is a bare RING, no fill at all, so the bite terrain takes
    // out of a circle is a difference in kind rather than a few percent of alpha.
    this.plateTexture = this.makePlateTexture();
    this.sightFillTexture = this.makeRangeTexture(0.1, 0.55);
    this.sightRingTexture = this.makeRangeTexture(0, 0.3);
    this.chevronTexture = this.makeChevronTexture();

    parent.appendChild(this.app.canvas);
    this.app.canvas.style.imageRendering = 'pixelated';
    this.app.canvas.style.transformOrigin = 'top left';
    this.app.canvas.style.position = 'absolute';
    this.resize();
  }

  /** A pre-baked circle, scaled per tower — not a Graphics redrawn each frame (R-03-23). */
  /**
   * The Aegis's frontal plate: a thick arc, drawn once and rotated per enemy.
   *
   * An arc rather than a ring, because the SHAPE is the information — a player has to be
   * able to see which side is protected without reading anything, and a symmetric mark
   * would say only "this one is special".
   *
   * Drawn pointing along +x so the sprite's rotation can be set straight from the enemy's
   * facing with no offset to remember.
   */
  private makePlateTexture(): Texture {
    const size = 64;
    const g = new Graphics();
    const r = size / 2 - 4;
    // 60° either side of dead ahead, matching the arc the sim actually tests.
    g.arc(size / 2, size / 2, r, -Math.PI / 3, Math.PI / 3);
    g.stroke({ width: 7, color: 0xffffff, alpha: 1, cap: 'round' });
    const rt = RenderTexture.create({ width: size, height: size });
    this.app.renderer.render({ container: g, target: rt });
    g.destroy();
    return rt;
  }

  private makeRangeTexture(fillAlpha = 0.06, strokeAlpha = 0.5): Texture {
    const size = 128;
    const g = new Graphics();
    if (fillAlpha > 0) {
      g.circle(size / 2, size / 2, size / 2 - 2).fill({ color: 0xffffff, alpha: fillAlpha });
    }
    g.circle(size / 2, size / 2, size / 2 - 2).stroke({ width: 2, color: 0xffffff, alpha: strokeAlpha });
    const rt = RenderTexture.create({ width: size, height: size });
    this.app.renderer.render({ container: g, target: rt });
    g.destroy();
    return rt;
  }

  /** A small right-pointing chevron for the path-direction flow (B-8). */
  private makeChevronTexture(): Texture {
    const g = new Graphics();
    g.poly([0, 0, 6, 4, 0, 8, 2, 4]).fill({ color: 0xffffff, alpha: 0.9 });
    const rt = RenderTexture.create({ width: 8, height: 8 });
    this.app.renderer.render({ container: g, target: rt });
    g.destroy();
    return rt;
  }

  // ── Map ─────────────────────────────────────────────────────────────────────────────

  /**
   * Bake the tilemap once into a single texture (TRD-03 R-03-06).
   *
   * The alternative — 576 live Sprites per layer — costs draw calls and memory forever in
   * exchange for flexibility the map never uses, because the terrain does not change
   * during a run.
   */
  setMap(map: MapView): void {
    this.mapData = map;
    // Mirrors `World.blocksSight` exactly, down to the out-of-bounds answer: off the board
    // is clear, not solid, or every circle would be clipped at the map edge. A FRESH
    // closure per map is what invalidates the range circles' cached sight polygons.
    this.blockedSight = (tileX, tileY) => map.buildMask[tileY]?.[tileX] === '#';
    this.bgLayer.removeChildren();
    this.overlayLayer.removeChildren();

    const bake = (layer: readonly number[], target: Container): void => {
      const scratch = new Container();
      for (let i = 0; i < layer.length; i++) {
        const index = layer[i] ?? 0;
        if (index === 0) continue;
        const texture = this.assets.tiles[index];
        if (!texture) continue;
        const s = new Sprite(texture);
        s.position.set((i % MAP_COLS) * TILE_PX, Math.floor(i / MAP_COLS) * TILE_PX);
        scratch.addChild(s);
      }
      const rt = RenderTexture.create({ width: LOGICAL_W, height: LOGICAL_H });
      this.app.renderer.render({ container: scratch, target: rt });
      scratch.destroy({ children: true });
      target.addChild(new Sprite(rt));
    };

    bake(map.groundLayer, this.bgLayer);
    bake(map.overlayLayer, this.overlayLayer);

    this.buildWaypointMarks(map);
  }

  /**
   * B-8 — "everybody asked what is start and ending." Spawns and the Core get pulsing
   * rings and plain-word labels, straight from the mask's 's'/'c' tiles (authored and
   * generated maps both mark them). Placeholder identity until real art lands.
   */
  private buildWaypointMarks(map: MapView): void {
    for (const m of this.spawnMarks) m.sprite.destroy();
    this.spawnMarks = [];
    if (this.coreMark) this.coreMark.sprite.destroy();
    this.coreMark = null;
    for (const l of this.wayLabels) l.destroy();
    this.wayLabels = [];
    for (const f of this.flow) f.sprite.destroy();
    this.flow = [];
    this.flowBuilt = false;

    const label = (text: string, tileX: number, tileY: number, tint: number): void => {
      const l = new Text({
        text,
        style: { fontFamily: 'monospace', fontSize: 6, fill: tint, fontWeight: 'bold' },
      });
      l.anchor.set(0.5, 1);
      // A spawn on the top row would push its label off-canvas — flip it below.
      const labelY = tileY === 0 ? (tileY + 2) * TILE_PX + 6 : tileY * TILE_PX - 2;
      l.position.set(tileX * TILE_PX + TILE_PX / 2, labelY);
      this.uiWorldLayer.addChild(l);
      this.wayLabels.push(l);
    };
    const ring = (tileX: number, tileY: number, tint: number, tiles: number) => {
      const s = new Sprite(this.rangeTexture);
      s.anchor.set(0.5, 0.5);
      s.tint = tint;
      const base = (tiles * TILE_PX) / 128;
      s.scale.set(base);
      s.position.set(tileX * TILE_PX + TILE_PX / 2, tileY * TILE_PX + TILE_PX / 2);
      this.rangeLayer.addChild(s);
      return { sprite: s, base };
    };

    // Buff tiles: a ring in the kind's own colour, so the map reads as having places
    // worth wanting before a single tower is bought.
    for (const m of this.buffMarks) m.sprite.destroy();
    this.buffMarks = [];
    for (const tile of map.buffTiles ?? []) {
      // A filled pad UNDER the ring. The ring alone was almost invisible against the
      // ground ramp, and a tile nobody notices is a mechanic nobody plays — the whole
      // point is that the good ground is readable before a tower is bought.
      const pad = new Sprite(Texture.WHITE);
      pad.tint = tile.color;
      pad.alpha = 0.22;
      pad.width = TILE_PX;
      pad.height = TILE_PX;
      pad.position.set(tile.x * TILE_PX, tile.y * TILE_PX);
      this.overlayLayer.addChild(pad);

      const mark = ring(tile.x, tile.y, tile.color, 1.5);
      mark.sprite.alpha = 0.8;
      this.buffMarks.push({ ...mark, x: tile.x, y: tile.y, color: tile.color, particle: tile.particle });
    }

    for (let y = 0; y < map.buildMask.length; y++) {
      const row = map.buildMask[y] ?? '';
      for (let x = 0; x < row.length; x++) {
        if (row[x] === 's') {
          this.spawnMarks.push(ring(x, y, 0x4cc9f0, 1.6));
          label('START', x, y, 0x4cc9f0);
        } else if (row[x] === 'c') {
          this.coreMark = ring(x, y, 0xe8c95a, 1.8);
          label('DEFEND ♥', x, y, 0xe8c95a);
        }
      }
    }
  }

  /**
   * Chevrons sampled along each path, lit as a travelling wave from spawn toward the
   * Core during the build phase — direction the player can read before anything walks.
   * Built lazily from the world's own waypoints (setMap has no path ORDER).
   */
  private buildPathFlow(world: World): void {
    this.flowBuilt = true;
    const SPACING = TILE_PX * 1.25;
    for (const path of world.config.paths) {
      const pts = path.waypoints;
      let carried = 0;
      let dist = 0;
      for (let i = 0; i + 1 < pts.length; i++) {
        const a = pts[i] as { x: number; y: number };
        const b = pts[i + 1] as { x: number; y: number };
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const seg = Math.hypot(dx, dy) * TILE_PX;
        if (seg === 0) continue;
        const angle = Math.atan2(dy, dx);
        let along = SPACING - carried;
        while (along <= seg) {
          const f = along / seg;
          const s = new Sprite(this.chevronTexture);
          s.anchor.set(0.5, 0.5);
          s.rotation = angle;
          s.alpha = 0;
          // Waypoints are tile CORNERS (integer coords) — shift half a tile so the
          // chevrons ride the middle of the drawn path, not its edge.
          s.position.set(
            (a.x + dx * f) * TILE_PX + TILE_PX / 2,
            (a.y + dy * f) * TILE_PX + TILE_PX / 2,
          );
          this.rangeLayer.addChild(s);
          this.flow.push({ sprite: s, dist: dist + along });
          along += SPACING;
        }
        carried = seg - (along - SPACING);
        dist += seg;
      }
    }
  }

  // ── Per-frame sync ──────────────────────────────────────────────────────────────────

  /**
   * Interpolate rendered positions between the previous and current tick (R-03-12).
   *
   * Interpolated values are written to SPRITES ONLY, never back into sim entities — that
   * would make presentation smoothing part of the simulation and break replay.
   */
  sync(world: World, alpha: number): void {
    this.syncEnemies(world, alpha);
    this.syncTowers(world);
    this.syncProjectiles(world, alpha);
    this.syncDrones(world);
    this.syncZones(world);
  }

  private syncEnemies(world: World, alpha: number): void {
    const seen = new Set<number>();

    for (const e of world.enemies.items) {
      if (!e.alive) continue;
      seen.add(e.id);

      let view = this.enemies.get(e.id);
      if (!view) {
        const key = enemyTextureKey(e.archetype);
        const texture = this.assets.enemy[key] ?? this.assets.enemy['enemy_runner'];
        const sprite = new Sprite(texture);
        sprite.anchor.set(0.5, 0.5);

        const bar = new Container();
        const bg = new Sprite(Texture.WHITE);
        bg.tint = 0x1a1a1d;
        bg.width = 10;
        bg.height = 2;
        const fill = new Sprite(Texture.WHITE);
        fill.tint = 0xc8503c;
        fill.width = 10;
        fill.height = 2;
        bar.addChild(bg, fill);
        bar.visible = false;

        // Only the finale glitches. Everything else pays nothing for this.
        let ghostA: Sprite | null = null;
        let ghostB: Sprite | null = null;
        if (e.archetype === GLITCH_ARCHETYPE) {
          ghostA = new Sprite(texture);
          ghostB = new Sprite(texture);
          ghostA.anchor.set(0.5, 0.5);
          ghostB.anchor.set(0.5, 0.5);
          ghostA.tint = 0xff006e;
          ghostB.tint = 0x4cc9f0;
          ghostA.blendMode = 'add';
          ghostB.blendMode = 'add';
          ghostA.alpha = 0.55;
          ghostB.alpha = 0.55;
          this.entityLayer.addChild(ghostA, ghostB);
        }

        let shieldRing: Sprite | null = null;
        if (e.maxShieldHp > 0) {
          shieldRing = new Sprite(this.sightRingTexture);
          shieldRing.anchor.set(0.5, 0.5);
          shieldRing.tint = 0x4fd8ff;
          shieldRing.blendMode = 'add';
          this.entityLayer.addChild(shieldRing);
        }

        view = {
          sprite,
          bar,
          barFill: fill,
          ghostA,
          ghostB,
          plate: null,
          shieldRing,
          pips: null,
          statusMask: 0,
          prevX: e.x,
          prevY: e.y,
          flashing: false,
        };
        this.enemies.set(e.id, view);
        this.entityLayer.addChild(sprite, bar);
      }

      const x = view.prevX + (e.x - view.prevX) * alpha;
      const y = view.prevY + (e.y - view.prevY) * alpha;

      // Snap to whole logical pixels. Sub-pixel positions on a 16px sprite shimmer, and
      // shimmer reads as a bug (R-03-13).
      const px = Math.round(x * TILE_PX);
      let py = Math.round(y * TILE_PX);

      // Rising out of the ground: the sprite climbs into its own footprint from below,
      // clipped by nothing — it simply starts underneath and arrives. Paired with the
      // crack ring the VFX layer draws at the same spot, it reads as the floor breaking.
      const emergeLeft = e.emergeUntilTick - world.tick;
      const emerging = emergeLeft > 0;
      if (emerging) {
        const span = Math.max(1, e.emergeUntilTick - e.spawnTick);
        const progress = 1 - emergeLeft / span;
        py += Math.round((1 - progress) * e.size * 2 * TILE_PX);
        view.sprite.alpha = 0.25 + 0.75 * progress;
      } else if (view.sprite.alpha !== 1) {
        view.sprite.alpha = 1;
      }

      view.sprite.position.set(px, py);
      view.sprite.zIndex = py;

      // Built the first frame the enemy turns out to have a plate, rather than when its
      // view was created. Creation-time was enough for an authored Aegis — it spawns with
      // the field set — but it silently drew nothing for an enemy that gained one later,
      // and a visual cue that depends on when a sprite happened to be constructed is one
      // that will eventually not be there when it matters.
      if (!view.plate && e.frontalCos > 0) {
        const built = new Sprite(this.plateTexture);
        built.anchor.set(0.5, 0.5);
        built.tint = 0xc9d6e4;
        this.entityLayer.addChild(built);
        view.plate = built;
      }

      // The plate points where the enemy is going. atan2 is fine HERE — this is the
      // renderer; the sim answers the same question with a dot product because it may not
      // call trigonometry at all.
      if (view.plate) {
        view.plate.visible = !emerging;
        view.plate.position.set(px, py);
        view.plate.zIndex = py + 1;
        view.plate.rotation = Math.atan2(e.facingY, e.facingX);
        const d = e.size * 2.6 * TILE_PX;
        view.plate.width = d;
        view.plate.height = d;
      }

      // The bubble holds while the bar does, and is simply gone while the shield is
      // down. A ring that faded out would read as "weakening" — the state is binary and
      // the drawing should be too.
      if (view.shieldRing) {
        const up = e.shieldHp > 0 && !emerging;
        view.shieldRing.visible = up;
        if (up) {
          // Breathes on the wall clock: pure presentation, never touches the sim.
          const pulse = 1 + Math.sin(this.markTime * 0.004) * 0.06;
          const diameter = e.size * 2.9 * TILE_PX * pulse;
          view.shieldRing.width = diameter;
          view.shieldRing.height = diameter;
          view.shieldRing.alpha = 0.55 + 0.2 * (e.shieldHp / e.maxShieldHp);
          view.shieldRing.position.set(px, py);
          view.shieldRing.zIndex = py - 1;
        }
      }

      // The glitch: ghosts jitter around the body every frame, and tear wider the moment
      // a teleport lands. Deliberately driven by wall-clock randomness — it is pure
      // presentation and never touches the simulation (TRD-04 R-04-01).
      if (view.ghostA && view.ghostB) {
        // Two scales of displacement: a constant shimmer, plus an occasional hard tear
        // that snaps the ghosts a long way sideways for a single frame. The stutter is
        // what separates "glitching" from "glowing" — a steady offset just reads as a
        // halo, and a boss that cheats should look like a dropped frame.
        const torn = this.glitchUntil > this.markTime;
        const shimmer = torn ? 6 : 2.5;
        const snap = Math.random() < (torn ? 0.5 : 0.08) ? (Math.random() - 0.5) * TILE_PX * 1.5 : 0;
        view.ghostA.position.set(
          px + snap + (Math.random() - 0.5) * 2 * shimmer,
          py + (Math.random() - 0.5) * shimmer,
        );
        view.ghostB.position.set(
          px - snap - (Math.random() - 0.5) * 2 * shimmer,
          py - (Math.random() - 0.5) * shimmer,
        );
        view.ghostA.zIndex = py - 1;
        view.ghostB.zIndex = py - 1;
        view.ghostA.texture = view.sprite.texture;
        view.ghostB.texture = view.sprite.texture;
        view.ghostA.alpha = emerging ? view.sprite.alpha * 0.55 : 0.55;
        view.ghostB.alpha = view.ghostA.alpha;
      }

      // Facing by horizontal flip only. Rotating pixel art at 16px is destructive (R-03-17).
      if (e.x > view.prevX + 0.0001) view.sprite.scale.x = 1;
      else if (e.x < view.prevX - 0.0001) view.sprite.scale.x = -1;

      // Damage flash by TEXTURE SWAP, not tint (TRD-04 R-04-15). Tinting a dark sprite
      // white is barely visible; swapping to a pre-generated white silhouette reads
      // instantly even in a crowded frame.
      const key = enemyTextureKey(e.archetype);
      const flashing = this.vfx?.isFlashing(e.id) ?? false;
      if (flashing !== view.flashing) {
        view.flashing = flashing;
        const texture = flashing ? this.assets.enemyFlash[key] : this.assets.enemy[key];
        if (texture) view.sprite.texture = texture;
      }

      const damaged = e.hp < e.maxHp;
      view.bar.visible = damaged;
      if (damaged) {
        const w = Math.max(1, Math.round(e.size * 2 * TILE_PX));
        const ratio = Math.max(0, e.hp / e.maxHp);
        (view.bar.children[0] as Sprite).width = w;
        view.barFill.width = Math.max(1, Math.round(w * ratio));
        view.bar.position.set(px - w / 2, py - Math.round(e.size * TILE_PX) - 4);
        view.bar.zIndex = py + 1;
      }

      this.syncStatusPips(view, e.statuses, px, py, Math.round(e.size * TILE_PX));

      view.prevX = e.x;
      view.prevY = e.y;
    }

    for (const [id, view] of this.enemies) {
      if (seen.has(id)) continue;
      view.ghostA?.destroy();
      view.ghostB?.destroy();
      view.plate?.destroy();
      view.shieldRing?.destroy();
      view.sprite.destroy();
      view.bar.destroy({ children: true });
      view.pips?.destroy({ children: true });
      this.enemies.delete(id);
    }
  }

  /**
   * Status pips above the health bar (R-03-16).
   *
   * R-03-16 asks for this to recompute on `statusApplied` and status-expiry events rather
   * than per frame. It recomputes on CHANGE instead, which is the property that rule is
   * protecting — no display-list work happens on a frame where nothing was applied or
   * expired — and it gets there without the two problems the event-driven version has.
   * There is no status-expiry event in `SimEvent` to key off, so one would have to be
   * added; and a purely event-driven cache drifts permanently if a single batch is ever
   * missed, whereas a mask diffed against world state is self-correcting every frame.
   *
   * The cost of the diff is seven integer compares per enemy on a loop that already reads
   * position and hp — far below the sprite churn the rule exists to prevent.
   */
  private syncStatusPips(
    view: EnemyView,
    statuses: StatusState,
    px: number,
    py: number,
    halfHeight: number,
  ): void {
    const mask = statusMaskOf(statuses);

    if (mask === 0) {
      // Keep the container once built. An enemy that has been poisoned once is very
      // likely to be poisoned again, and rebuilding four sprites per re-application is
      // exactly the churn this is avoiding.
      if (view.pips) view.pips.visible = false;
      view.statusMask = 0;
      return;
    }

    if (!view.pips) {
      const container = new Container();
      for (let i = 0; i < MAX_STATUS_PIPS; i++) {
        const pip = new Sprite(Texture.WHITE);
        pip.width = PIP_PX;
        pip.height = PIP_PX;
        pip.position.set(i * (PIP_PX + PIP_GAP), 0);
        pip.visible = false;
        container.addChild(pip);
      }
      view.pips = container;
      this.entityLayer.addChild(container);
    }

    view.pips.visible = true;

    if (mask !== view.statusMask) {
      view.statusMask = mask;
      const count = pipTints(mask, this.pipScratch);
      const children = view.pips.children;
      for (let i = 0; i < MAX_STATUS_PIPS; i++) {
        const pip = children[i] as Sprite;
        pip.visible = i < count;
        if (i < count) pip.tint = this.pipScratch[i] as number;
      }
      // A partial row is centred as a row of its own, not left-aligned inside a full one,
      // or a single pip sits off to the enemy's left and reads as belonging to a neighbour.
      view.pips.pivot.x = (count * PIP_PX + (count - 1) * PIP_GAP) / 2;
    }

    // Above the health bar: the bar occupies 2 px at -4, so the pip row sits at -8.
    view.pips.position.set(px, py - halfHeight - 8);
    // Sorted by the enemy's own depth, one above its bar — pips belong to the enemy they
    // sit over, and must not sort behind an enemy standing further down the path.
    view.pips.zIndex = py + 2;
  }

  private syncTowers(world: World): void {
    const seen = new Set<number>();

    for (const t of world.towers.items) {
      if (!t.alive) continue;
      seen.add(t.id);

      let view = this.towers.get(t.id);
      if (!view) {
        view = new TowerView(this.assets);
        this.towers.set(t.id, view);
        this.entityLayer.addChild(view.container);
      }

      const visual = this.lookup(t.defId);
      view.apply({ ...visual, defId: t.defId, star: t.star });
      view.setTile(t.tileX, t.tileY);
      view.setHighlight(
        this.hoveredTower === t.id ? 'hover' : this.classHighlight.has(t.id) ? 'selected' : 'none',
      );
      view.setMergeHint(this.mergeHints.has(t.id));

      // Dark, plus a ring so a dimmed tower cannot be mistaken for one that merely has
      // nothing to shoot at. Sized to the tower rather than to the blast — the blast is
      // gone, what remains is which towers it took.
      const disabled = t.disabledUntilTick > world.tick;
      view.setDisabled(disabled);
      let hex = this.disableRings.get(t.id);
      if (disabled && !hex) {
        hex = new Sprite(this.sightRingTexture);
        hex.anchor.set(0.5, 0.5);
        hex.tint = 0x9b5de5;
        hex.blendMode = 'add';
        this.disableRings.set(t.id, hex);
        this.rangeLayer.addChild(hex);
      }
      if (hex) {
        hex.visible = disabled;
        if (disabled) {
          const pulse = 1 + Math.sin(this.markTime * 0.02) * 0.12;
          const d = TILE_PX * 1.5 * pulse;
          hex.width = d;
          hex.height = d;
          hex.alpha = 0.9;
          hex.position.set(t.tileX * TILE_PX + TILE_PX / 2, t.tileY * TILE_PX + TILE_PX / 2);
        }
      }

      // A surging support tower wears a bright ring for its window. The aura it is
      // paying out is invisible by nature — it changes numbers on OTHER towers — so
      // without this the one action the player takes mid-wave has no on-board result.
      const surging = world.phase === 'combat' && t.surgeWave === world.wave;
      let ring = this.surgeRings.get(t.id);
      if (surging && !ring) {
        ring = new Sprite(this.sightRingTexture);
        ring.anchor.set(0.5, 0.5);
        ring.blendMode = 'add';
        this.surgeRings.set(t.id, ring);
        this.rangeLayer.addChild(ring);
      }
      if (ring) {
        ring.visible = surging;
        if (surging) {
          const pulse = 1 + Math.sin(this.markTime * 0.012) * 0.1;
          // Sized to the AURA, not the sprite: what the ring marks is the reach that is
          // currently paying out, which is the only part the player can act on.
          const diameter = starRange(t) * 2 * TILE_PX * pulse;
          ring.width = diameter;
          ring.height = diameter;
          ring.tint = this.lookup(t.defId).colorB;
          ring.alpha = 0.85;
          ring.position.set(t.tileX * TILE_PX + TILE_PX / 2, t.tileY * TILE_PX + TILE_PX / 2);
        }
      }
    }

    for (const [id, view] of this.towers) {
      if (seen.has(id)) continue;
      view.destroy();
      this.surgeRings.get(id)?.destroy();
      this.surgeRings.delete(id);
      this.disableRings.get(id)?.destroy();
      this.disableRings.delete(id);
      this.towers.delete(id);
    }
  }

  /**
   * Draw every live hitscan line into the one shared Graphics (TRD-03 §10).
   *
   * Redrawn from scratch each frame rather than retained: a Graphics per beam would mean
   * an allocation and a geometry rebuild for every shot from every laser tower, which is
   * exactly the Pixi mistake R-03-05 warns about. One clear and N strokes is cheap.
   *
   * Each line is two strokes — a wide dim glow under a thin bright core. That is what
   * separates a beam from a drawn line at 16 px per tile, where a single 1 px stroke
   * reads as a UI artefact rather than as a weapon.
   */
  private drawBeams(deltaMs: number): void {
    this.beams.clear();

    for (let i = this.beamLines.length - 1; i >= 0; i--) {
      const b = this.beamLines[i] as BeamLine;
      b.lifeMs -= deltaMs;
      if (b.lifeMs <= 0) {
        this.beamLines.splice(i, 1);
        continue;
      }

      // A laser holds full strength while it is firing and only fades once the sim stops
      // refreshing it. A flash decays from the moment it lands.
      const t = b.lifeMs / b.maxLifeMs;
      const alpha = b.weapon === 'laser' ? Math.min(1, t * 3) : t;
      // A chain arc gets thinner every hop, so the falloff is legible without numbers.
      const scale = b.chainHop > 0 ? 1 / (1 + b.chainHop * 0.5) : 1;

      this.strokeBeamPath(b);
      this.beams.stroke({ width: 3 * scale, color: b.color, alpha: alpha * 0.25 });
      this.strokeBeamPath(b);
      this.beams.stroke({ width: 1 * scale, color: BEAM_CORE, alpha });
    }
  }

  /**
   * Trace one line's path into the shared Graphics: straight for `beam` and `laser`, a
   * jittered polyline for `chain` (R-03-20).
   *
   * Interior vertices sit at even fractions along the segment, displaced perpendicular
   * to it by the offsets rolled when the arc landed. Displacing along the PERPENDICULAR
   * is what makes it read as a bolt rather than a wobbly line — jitter along the segment
   * direction only changes vertex spacing, which the eye cannot see on a stroke.
   */
  private strokeBeamPath(b: BeamLine): void {
    this.beams.moveTo(b.x0, b.y0);

    const n = b.jitter.length;
    if (n > 0) {
      const dx = b.x1 - b.x0;
      const dy = b.y1 - b.y0;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len > 1) {
        // Unit perpendicular; jitter offsets are already in pixels.
        const px = -dy / len;
        const py = dx / len;
        for (let i = 0; i < n; i++) {
          const f = (i + 1) / (n + 1);
          const off = b.jitter[i] as number;
          this.beams.lineTo(b.x0 + dx * f + px * off, b.y0 + dy * f + py * off);
        }
      }
    }

    this.beams.lineTo(b.x1, b.y1);
  }

  private trailAccumulator = 0;

  private syncProjectiles(world: World, alpha: number): void {
    const seen = new Set<number>();

    for (const p of world.projectiles.items) {
      if (!p.alive) continue;
      seen.add(p.id);

      let sprite = this.projectiles.get(p.id);
      if (!sprite) {
        // Silhouette by role, not by damage kind: a missile and a mortar shell have to be
        // told apart at a glance, and they are the two the player most needs to read.
        const art = p.aoeRadius > 0 ? (p.turnRate > 0 ? 'proj_orb' : 'proj_shard') : 'proj_bullet';
        sprite = new Sprite(this.assets.sprite[art] as Texture);
        sprite.anchor.set(0.5, 0.5);
        this.projectiles.set(p.id, sprite);
        this.projectileLayer.addChild(sprite);
      }

      const x = (p.x + p.vx * alpha * (1 / 60)) * TILE_PX;
      const y = (p.y + p.vy * alpha * (1 / 60)) * TILE_PX;
      sprite.position.set(Math.round(x), Math.round(y));
      sprite.rotation = Math.atan2(p.vy, p.vx);

      // Trails. `Vfx.emitTrail` existed from the start and nothing ever called it, so
      // every shot in the game crossed the board as a silent sprite. Emitted from the
      // renderer because this is the only loop that walks live projectiles every FRAME —
      // the sim only reports the moment a shot lands, and a trail is about the journey.
      //
      // Rate-limited by wall-clock rather than per frame: a screen with thirty shots in
      // the air would otherwise spend the whole particle pool on trails and have nothing
      // left for the impacts.
      this.trailAccumulator += 1;
      if (this.trailAccumulator % TRAIL_EVERY_N_FRAMES === 0) {
        const heavy = p.aoeRadius > 0;
        this.vfx?.emitTrail(p.x, p.y, heavy ? 0xff8f1f : 0xdbe9ff);
      }
    }

    for (const [id, sprite] of this.projectiles) {
      if (seen.has(id)) continue;
      sprite.destroy();
      this.projectiles.delete(id);
    }
  }

  /**
   * Drones (B-17) — a small mote in the owner's tint, so a broodmother's brood reads as
   * HERS from across the board. Drones were sim-real but invisible before this; a tower
   * whose whole identity is summoning cannot ship with an invisible summon.
   */
  private syncDrones(world: World): void {
    const seen = new Set<number>();

    for (const d of world.drones.items) {
      if (!d.alive) continue;
      seen.add(d.id);

      let sprite = this.droneSprites.get(d.id);
      if (!sprite) {
        sprite = new Sprite(this.assets.sprite['proj_orb'] as Texture);
        sprite.anchor.set(0.5, 0.5);
        sprite.scale.set(0.75);
        const owner = world.towers.get(d.ownerTower);
        sprite.tint = owner ? this.lookup(owner.defId).colorA : 0xffffff;
        this.droneSprites.set(d.id, sprite);
        this.projectileLayer.addChild(sprite);
      }

      sprite.position.set(Math.round(d.x * TILE_PX), Math.round(d.y * TILE_PX));
    }

    for (const [id, sprite] of this.droneSprites) {
      if (seen.has(id)) continue;
      sprite.destroy();
      this.droneSprites.delete(id);
    }
  }

  /**
   * Zones (B-18/B-19) — the same pre-baked circle the range indicators use, in the
   * damage-kind tint. Telegraph reads dim and breathing; the active zone snaps bright
   * on each strike and decays toward the next, so the cadence is visible without
   * numbers. Position is re-set every sync because a tracking zone moves.
   */
  private syncZones(world: World): void {
    const seen = new Set<number>();

    for (const z of world.zones.items) {
      if (!z.alive) continue;
      seen.add(z.id);

      let sprite = this.zoneSprites.get(z.id);
      if (!sprite) {
        sprite = new Sprite(this.rangeTexture);
        sprite.anchor.set(0.5, 0.5);
        sprite.tint = BEAM_TINT[z.kind];
        this.zoneSprites.set(z.id, sprite);
        this.rangeLayer.addChild(sprite);
      }

      sprite.position.set(z.x * TILE_PX, z.y * TILE_PX);

      if (z.windupLeft > 0) {
        // Telegraph: slightly small, dimmer, flickering as the countdown runs — "get out".
        sprite.scale.set((z.radius * 2 * TILE_PX * 0.9) / 128);
        sprite.alpha = 0.35 + 0.12 * Math.sin(z.windupLeft * 0.6);
      } else {
        sprite.scale.set((z.radius * 2 * TILE_PX) / 128);
        // Bright at the strike (sinceStrike 0), decaying toward the next.
        const cycle = z.sinceStrike / Math.max(1, z.strikeEveryTicks);
        sprite.alpha = 0.9 - 0.45 * Math.min(1, cycle);
      }
    }

    for (const [id, sprite] of this.zoneSprites) {
      if (seen.has(id)) continue;
      sprite.destroy();
      this.zoneSprites.delete(id);
    }
  }

  /**
   * Create the VFX binding. Called once, after assets are loaded.
   *
   * The renderer owns the layers, so it owns their wiring — but the VFX *simulation*
   * stays outside Pixi entirely, which is what lets the determinism test run headless.
   */
  attachVfx(vfx: Vfx): void {
    this.vfx = vfx;
    this.vfxRenderer = new VfxRenderer(
      this.assets,
      this.particleLayer,
      this.uiWorldLayer,
      this.screenLayer,
      LOGICAL_W,
      LOGICAL_H,
    );
  }

  /** Presentation tweens, driven by REAL time (TRD-04 R-04-02). */
  update(deltaMs: number, world: World): void {
    for (const [id, view] of this.towers) {
      const t = world.towers.get(id);
      if (t) view.update(deltaMs, t.tileX, t.tileY);
    }
    this.drawBeams(deltaMs);
    this.updateWaypointMarks(deltaMs, world);
    this.updateBuffTiles(deltaMs, world);

    if (this.vfx) {
      const offset = this.vfx.shake.offset;
      this.world.position.set(LOGICAL_W / 2 + offset.x, LOGICAL_H / 2 + offset.y);
      this.world.rotation = offset.rotation;
      this.vfxRenderer?.sync(this.vfx);
    }
  }

  /**
   * B-8's living layer — a FIRST-ROUND tutorial: rings, labels and the path flow show
   * only during wave 0's build phase. Once the first wave starts the player has seen
   * where things come from and go, and the guides would just be clutter after that.
   */
  /**
   * Shimmer the buff tiles, and give the tower standing on one the SAME particle.
   *
   * The tower carrying the tile's effect is the point: "you should always know", so the
   * buff is legible from the board alone rather than only by selecting the tower. The
   * emitter is throttled on wall-clock rather than per frame, because at 4x speed a
   * per-frame trickle from four tiles would spend the particle pool on ambience and
   * leave nothing for the impacts.
   */
  private updateBuffTiles(deltaMs: number, world: World): void {
    if (this.buffMarks.length === 0) return;
    this.buffPulse += deltaMs;

    for (const mark of this.buffMarks) {
      // Gentle breathing, out of phase per tile so a row of them does not blink in unison.
      const phase = (this.buffPulse / 900 + mark.x * 0.37 + mark.y * 0.11) % 1;
      const swell = 1 + 0.12 * Math.sin(phase * Math.PI * 2);
      mark.sprite.scale.set(mark.base * swell);
      mark.sprite.alpha = 0.6 + 0.25 * Math.sin(phase * Math.PI * 2);
    }

    if (this.buffPulse < 220) return;
    this.buffPulse = 0;

    for (const mark of this.buffMarks) {
      this.vfx?.emitTilePresence(mark.particle, mark.x + 0.5, mark.y + 0.5, mark.color);
    }
    // ...and again on whoever is standing there.
    const towers = world.towers.items;
    for (let i = 0; i < towers.length; i++) {
      const t = towers[i];
      if (!t || !t.alive) continue;
      const mark = this.buffMarks.find((m) => m.x === t.tileX && m.y === t.tileY);
      if (mark) this.vfx?.emitTilePresence(mark.particle, t.x, t.y, mark.color);
    }
  }

  private updateWaypointMarks(deltaMs: number, world: World): void {
    this.markTime += deltaMs;
    if (!this.flowBuilt && world.config.paths.length > 0) this.buildPathFlow(world);

    const show = world.phase === 'build' && world.wave === 0;
    const pulse = 1 + 0.08 * Math.sin(this.markTime / 260);
    for (const m of this.spawnMarks) {
      m.sprite.scale.set(m.base * pulse);
      m.sprite.alpha = show ? 0.9 : 0;
    }
    if (this.coreMark) {
      this.coreMark.sprite.scale.set(this.coreMark.base * (2 - pulse));
      this.coreMark.sprite.alpha = show ? 0.9 : 0;
    }
    for (const l of this.wayLabels) l.visible = show;

    // A wave of brightness travelling spawn→Core.
    const WAVELENGTH = TILE_PX * 10;
    const SPEED = TILE_PX * 2.2; // px per second
    for (const f of this.flow) {
      if (!show) {
        f.sprite.alpha = 0;
        continue;
      }
      const phase = (f.dist - (this.markTime / 1000) * SPEED) / WAVELENGTH;
      const s = Math.sin(phase * Math.PI * 2);
      f.sprite.alpha = 0.08 + 0.4 * Math.max(0, s) * Math.max(0, s);
    }
  }

  /** React to sim events. Never reach into world state to answer "where" (TRD-00 §7). */
  handleEvents(events: readonly SimEvent[], world: World): void {
    for (const ev of events) {
      if (ev.t === 'towerFired') {
        const view = this.towers.get(ev.tower);
        const target = world.enemies.get(ev.target);
        if (view && target) view.onFired(target.x, target.y);
        continue;
      }

      if (ev.t === 'enemyTeleported') {
        // Tear at BOTH ends — the point of the read is that it was over there a frame ago.
        this.glitchUntil = this.markTime + 260;
        this.beamLines.push({
          tower: ev.enemy,
          x0: ev.x0 * TILE_PX,
          y0: ev.y0 * TILE_PX,
          x1: ev.x1 * TILE_PX,
          y1: ev.y1 * TILE_PX,
          color: 0xff006e,
          weapon: 'beam',
          chainHop: 0,
          lifeMs: 200,
          maxLifeMs: 200,
          jitter: [6, -8, 5, -4],
        });
        continue;
      }

      if (ev.t === 'beamFired') {
        // A sustained laser refreshes the SAME line rather than stacking a new one every
        // damage tick — otherwise a ten-second channel accumulates a hundred overlapping
        // segments, and the additive blend turns the beam into a white bar.
        const sustained = ev.weapon === 'laser';
        const existing = sustained
          ? this.beamLines.find((b) => b.tower === ev.tower && b.weapon === 'laser')
          : undefined;
        const line = existing ?? ({ jitter: [] } as unknown as BeamLine);

        line.tower = ev.tower;
        line.x0 = ev.x0 * TILE_PX;
        line.y0 = ev.y0 * TILE_PX;
        line.x1 = ev.x1 * TILE_PX;
        line.y1 = ev.y1 * TILE_PX;
        line.color = BEAM_TINT[ev.kind];
        line.weapon = ev.weapon;
        line.chainHop = ev.chainHop;
        // A one-shot flash gets its own decay; a laser lives exactly as long as the sim
        // said it should, so the line goes out the moment the beam stops firing.
        line.lifeMs = sustained ? (ev.holdTicks * 1000) / 60 : BEAM_FLASH_MS;
        line.maxLifeMs = line.lifeMs;

        // The lightning shape is rolled here, once per arc, and held for its lifetime
        // (R-03-20). Presentation-only randomness, so Math.random is correct and required
        // — the sim's RNG must never see a draw whose count depends on rendering.
        line.jitter.length = 0;
        if (ev.weapon === 'chain') {
          const n =
            CHAIN_JITTER_MIN +
            Math.floor(Math.random() * (CHAIN_JITTER_MAX - CHAIN_JITTER_MIN + 1));
          for (let i = 0; i < n; i++) {
            line.jitter.push((Math.random() * 2 - 1) * CHAIN_JITTER_PX);
          }
        }

        if (!existing) this.beamLines.push(line);
      }
    }
  }

  // ── Placement affordances (TRD-03 §9) ───────────────────────────────────────────────

  setGhost(
    defId: string | null,
    tileX: number,
    tileY: number,
    valid: boolean,
    range: number,
  ): void {
    if (defId === null) {
      if (this.ghost) this.ghost.visible = false;
      if (this.ghostRange) this.ghostRange.hide();
      return;
    }

    if (!this.ghost) {
      // The ghost is the ACTUAL tower being placed — base + ring tints and the glyph,
      // exactly like TowerView draws it — at half alpha. A generic grey box made
      // players ask which tower they were holding (backlog B-7).
      this.ghost = new Container();
      this.ghostBase = new Sprite(this.assets.sprite['tower_base'] as Texture);
      this.ghostRing = new Sprite(this.assets.sprite['tower_ring'] as Texture);
      this.ghostGlyph = new Text({
        text: '',
        style: { fontFamily: 'monospace', fontSize: 7, fill: 0xffffff },
      });
      this.ghostGlyph.anchor.set(0.5, 0.5);
      this.ghostGlyph.position.set(TILE_PX / 2, TILE_PX / 2 + 1);
      this.ghost.addChild(this.ghostBase, this.ghostRing, this.ghostGlyph);
      this.ghost.alpha = 0.55;
      this.uiWorldLayer.addChild(this.ghost);
    }
    if (!this.ghostRange) this.ghostRange = new RangeCircle(this.sightFillTexture, this.sightRingTexture, this.rangeLayer);

    if (this.ghostDef !== defId && this.ghostBase && this.ghostRing && this.ghostGlyph) {
      this.ghostDef = defId;
      const visual = this.lookup(defId);
      this.ghostBase.tint = visual.colorA;
      this.ghostRing.tint = visual.colorB;
      this.ghostGlyph.text = visual.glyph;
    }

    this.ghost.visible = true;
    this.ghost.position.set(tileX * TILE_PX, tileY * TILE_PX);
    // Invalid tiles override identity with the refusal red — the "no" must win.
    if (this.ghostBase && this.ghostRing) {
      if (valid) {
        const visual = this.lookup(defId);
        this.ghostBase.tint = visual.colorA;
        this.ghostRing.tint = visual.colorB;
      } else {
        this.ghostBase.tint = 0xff4444;
        this.ghostRing.tint = 0xff4444;
      }
    }

    // Hide the range when placement is illegal — showing coverage you cannot have is
    // worse than showing nothing (R-03-22).
    if (valid) this.ghostRange.show(tileX, tileY, range, this.blockedSight);
    else this.ghostRange.hide();
  }

  setHoveredTower(id: number | null): void {
    this.hoveredTower = id;
  }

  /** Placed towers whose merge is ready (B-5) — a ★ badge pulses over each. */
  setMergeHints(ids: readonly number[]): void {
    this.mergeHints.clear();
    for (const id of ids) this.mergeHints.add(id);
  }

  /**
   * Show one tower's range circle — hovering or selecting a tower on the field must
   * answer "what does this cover" without opening anything. Takes plain data like
   * setClassHighlight: the EFFECTIVE range comes from the snapshot (R-03-24).
   */
  setFocusRange(entry: { tileX: number; tileY: number; range: number } | null): void {
    if (!entry) {
      if (this.focusRange) this.focusRange.hide();
      return;
    }
    if (!this.focusRange) this.focusRange = new RangeCircle(this.sightFillTexture, this.sightRingTexture, this.rangeLayer);
    this.focusRange.show(entry.tileX, entry.tileY, entry.range, this.blockedSight);
  }

  /**
   * Range circles for every tower of a hovered synergy class (TRD-03 R-03-24).
   *
   * "The primary way a player reads their board composition" — towers are identical
   * shapes, so hovering `Frost 4` and seeing exactly which four towers light up and what
   * they cover is how the board is read at all.
   *
   * Takes plain data rather than reading world state: the effective range is computed by
   * the sim and published on the snapshot, and the HUD emits it as renderer state
   * (R-03-24's own wording). Circles are pooled sprites of the shared range texture —
   * a Graphics redrawn per hover would be the R-03-05 mistake with extra steps.
   */
  setClassHighlight(
    towers: readonly { id: number; tileX: number; tileY: number; range: number }[] | null,
  ): void {
    this.classHighlight.clear();

    const entries = towers ?? [];
    for (const t of entries) this.classHighlight.add(t.id);

    while (this.classRangeSprites.length < entries.length) {
      this.classRangeSprites.push(new RangeCircle(this.sightFillTexture, this.sightRingTexture, this.rangeLayer));
    }

    for (let i = 0; i < this.classRangeSprites.length; i++) {
      const circle = this.classRangeSprites[i] as RangeCircle;
      const entry = entries[i];
      if (!entry) circle.hide();
      else circle.show(entry.tileX, entry.tileY, entry.range, this.blockedSight);
    }
  }

  // ── Coordinates (TRD-03 R-03-04) ────────────────────────────────────────────────────
  // The ONLY coordinate conversion in the codebase. Every input path routes through here.

  screenToTile(clientX: number, clientY: number): { tileX: number; tileY: number } | null {
    const rect = this.app.canvas.getBoundingClientRect();
    const lx = (clientX - rect.left) / this.scale;
    const ly = (clientY - rect.top) / this.scale;
    if (lx < 0 || ly < 0 || lx >= LOGICAL_W || ly >= LOGICAL_H) return null;
    return { tileX: Math.floor(lx / TILE_PX), tileY: Math.floor(ly / TILE_PX) };
  }

  resize(): void {
    const parent = this.app.canvas.parentElement;
    if (!parent) return;
    const scale = computeScale(parent.clientWidth, parent.clientHeight);
    this.scale = scale;

    // CSS transform, not a renderer resize — resizing the renderer would change the
    // logical resolution and break the pixel grid (R-03-03).
    this.app.canvas.style.transform = `scale(${scale})`;
    this.app.canvas.style.left = `${Math.round((parent.clientWidth - LOGICAL_W * scale) / 2)}px`;
    this.app.canvas.style.top = `${Math.round((parent.clientHeight - LOGICAL_H * scale) / 2)}px`;
  }

  // ── Debug overlay (TRD-03 §11) ──────────────────────────────────────────────────────

  drawDebug(world: World): void {
    this.debug.clear();
    if (!this.debugVisible || !this.mapData) return;

    // Buildable mask.
    for (let y = 0; y < MAP_ROWS; y++) {
      const row = this.mapData.buildMask[y];
      if (!row) continue;
      for (let x = 0; x < MAP_COLS; x++) {
        if (row[x] !== '.') continue;
        this.debug
          .rect(x * TILE_PX + 7, y * TILE_PX + 7, 2, 2)
          .fill({ color: 0x4cc9f0, alpha: 0.35 });
      }
    }

    // Target lines. These will save hours when combat looks wrong.
    for (const t of world.towers.items) {
      if (!t.alive || t.targetId < 0) continue;
      const target = world.enemies.get(t.targetId);
      if (!target) continue;
      this.debug
        .moveTo(t.tileX * TILE_PX + TILE_PX / 2, t.tileY * TILE_PX + TILE_PX / 2)
        .lineTo(target.x * TILE_PX, target.y * TILE_PX)
        .stroke({ width: 1, color: 0xff006e, alpha: 0.6 });
    }
  }

  destroy(): void {
    for (const v of this.towers.values()) v.destroy();
    for (const v of this.enemies.values()) {
      v.sprite.destroy();
      v.bar.destroy({ children: true });
    }
    for (const s of this.projectiles.values()) s.destroy();
    this.towers.clear();
    this.enemies.clear();
    this.projectiles.clear();
    this.vfxRenderer?.destroy();
    this.app.destroy(true, { children: true });
  }
}
